import { z } from 'zod'

import { isoDateTime } from '../lib/date'
import type { ClinicId } from './clinic'
import { regionIdSchema } from './region'

// ---------------------------------------------------------------------------
// Catalog products — universal product registry
// ---------------------------------------------------------------------------

/**
 * A catalog product is an entry in the universal product registry —
 * every product/drug that can be prescribed, whether or not we carry
 * it in our own inventory. Products we stock have a corresponding row
 * in the `products` (inventory) table with pricing and stock details.
 */
export const catalogProductSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  formulation: z.string(),
  region: regionIdSchema,
  unit: z.string().nullable(),
  isControlledSubstance: z.boolean(),
  archivedAt: isoDateTime.nullable(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
})

export type CatalogProduct = z.output<typeof catalogProductSchema>

export const PRODUCT_TYPES = ['vaccine', 'medication', 'consumable', 'service'] as const
export const productTypeSchema = z.enum(PRODUCT_TYPES)
export type ProductType = z.infer<typeof productTypeSchema>

export const PRODUCT_CATEGORIES = [
  'Analgesics (painkillers)',
  'Anesthetics',
  'Antibiotics',
  'Antiemetics',
  'Antiepileptics',
  'Antifungals',
  'Antihistaminics',
  'Antimicrobials',
  'Antiseptics - Disinfectants - Cleaners',
  'Blood Work',
  'Bordetella',
  'Calming Supplements',
  'Cardiovascular Drugs',
  'Chemotherapy',
  'Dentistry',
  'Dermatologic Preparations',
  'Dewormers & Parasiticides',
  'DHPP/DAPP/DA2PP/DHLPP',
  'Diuretics',
  'Dry Food',
  'Dietary Supplements',
  'Ear Washes & Medication',
  'Endocrine',
  'Euthanasia',
  'Eye Washes & Medication',
  'Eyes & Ears Care',
  'Fluids & I.V. Solutions',
  'FVRCP',
  'General Vaccinations, Non Specific',
  'Insulin',
  'Itch Relief',
  'Leptospirosis',
  'Leukemia',
  'Microchips',
  'NSAID (Nonsteroidal anti-inflammatory)',
  'Official Certificates & Documents',
  'Other Consumables, White Goods, Hospital Supplies, & Exam Supplies',
  'Other Prescription Medicine',
  'Others',
  'Parasite Control',
  'Parasitology',
  'Pre/Probiotic',
  'Prescription Dry Food',
  'Prescription Wet Food',
  'Rabies',
  'Rapid Tests',
  'Reproductive System',
  'RX Flea & Tick Prevention (Non Heartworm)',
  'Sedatives',
  'Services - Dental',
  'Services - Diagnostic',
  'Services - Procedure',
  'Shampoos & Conditioners',
  'Steroids',
  'Syringes & Needles',
  'Thyroid Treatment',
  'Training & Behaviour',
  'Urinary Solutions',
]

export const productCategorySchema = z.enum(PRODUCT_CATEGORIES)
export type ProductCategory = z.infer<typeof productCategorySchema>

export const productSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  sku: z.string(),
  /** Region this product belongs to — determines its currency and availability. */
  region: regionIdSchema,
  /** System-level product type — drives inventory behaviour and contextual UI. */
  type: productTypeSchema,
  /** The smallest dispensable quantity — e.g. 'tablet', 'ml', 'capsule', 'dose'. */
  unit: z.string(),
  /** Number of dispensable units per pack — e.g. 100 tablets per box. */
  packSize: z.number().int(),
  /** The purchasable packaging — e.g. 'box', 'bottle', 'pack', 'vial'. */
  packUnit: z.string(),
  /** Acquisition cost per pack, in smallest currency unit (e.g. pence, cents). Known as COGS in the UI. */
  packCost: z.number().int(),
  /** Fixed selling price per pack, in smallest currency unit. When set, overrides the dynamic markup calculation. Nullable — allows temporary price fixing to shield customers from supplier fluctuations. At least one of packFixedPrice or markupBps must be set. */
  packFixedPrice: z.number().int().nullable(),
  /** Markup over pack cost in basis points (e.g. 1000 = 10%). Used to compute selling price dynamically. Ignored when packFixedPrice is also set. At least one of packFixedPrice or markupBps must be set. */
  markupBps: z.number().int().nullable(),
  /** VAT rate in basis points (e.g. 2000 = 20%). */
  vatBps: z.number().int(),
  manufacturer: z.string().nullable(),
  supplier: z.string().nullable(),
  isControlledSubstance: z.boolean(),
  requiresPrescription: z.boolean(),
  category: productCategorySchema,
  /** Stock level below which this product is flagged as low — expressed in dispensable units. */
  lowStockThreshold: z.number().int(),
  /** Whether stock for this product is tracked by batch/lot number. */
  hasBatchNumber: z.boolean(),
  /** Included at no charge with a flea, worm or tick membership plan. */
  fleaWormTick: z.boolean(),
  isActive: z.boolean(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
})

export type Product = z.output<typeof productSchema>

/** Backend-computed derived fields added to products in API responses. */
export const productDerivedFieldsSchema = z.object({
  /** Per-dispensable-unit selling price in smallest currency unit, derived from pack pricing. */
  unitPrice: z.number().int(),
  /** Human-readable pack description, e.g. "100 tablets / box". */
  packLabel: z.string(),
})

export type ProductDerivedFields = z.output<typeof productDerivedFieldsSchema>

export interface ProductStockByClinic {
  clinicId: ClinicId
  clinicName: string
  available: number
}
